import * as apiCompany from 'api/endpoints/company/company.api';
import * as CompanyTypes from './company.types';
import { notificationErr } from 'views/components/notification';
import { companyError } from 'utils/errorCodes';
import { messageError, messageSuccess } from 'views/components/message';
import { ITranslate } from 'interfaces/general.interface';
import {
  IupdateCompanyDataAPIBody,
  TSocialMedia,
} from 'api/endpoints/company/company.response.interface';
import { TcompanyDispatchReducer } from './company.reducer';
import { ICompanyUpdateFields } from 'interfaces/company.interface';
import { IgetClientsAPIResponseDataItem } from 'api/endpoints/superAdmin/superAdmin.response.interface';

export async function getCompanyDataAction({
  dispatch,
  clientId,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TcompanyDispatchReducer>;
  clientId: string;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  dispatch({ type: CompanyTypes.SET_IS_LOADING, payload: true });
  try {
    const companyFetch = await apiCompany.getCompanyInfoAPI({ clientId, token });
    if (companyFetch.response.status === 200) {
      dispatch({
        type: CompanyTypes.GET_COMPANY_DATA,
        payload: {
          company: companyFetch.data,
        },
      });
      return true;
    }
    notificationErr(companyError.getCompanyData_002, translate);
    return false;
  } catch (err) {
    notificationErr(companyError.getCompanyData_001, translate);
    return false;
  } finally {
    dispatch({ type: CompanyTypes.SET_IS_LOADING, payload: false });
  }
}

export async function updateCompanyAction({
  dispatch,
  companyId,
  companyData,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TcompanyDispatchReducer>;
  companyId: string;
  companyData: ICompanyUpdateFields;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  const newSocialMedia = companyData.new_social_media;
  delete companyData.new_social_media;
  const body: IupdateCompanyDataAPIBody = {
    filter: { id: companyId },
    update: companyData,
  };

  try {
    const companyFetch = await apiCompany.updateCompanyDataAPI({ body, token });
    if (companyFetch.data[0] === 1) {
      messageSuccess(translate('action_user_update-company-action_success'));
      dispatch({
        type: CompanyTypes.UPDATE_COMPANY,
        payload: companyData,
      });
      return true;
    }
    if (companyFetch.response.status === 404) {
      messageError(translate('action_user_update-company-action_error-1'));
      notificationErr(companyError.update_company_001, translate);
      return false;
    }
    notificationErr(companyError.update_company_003, translate);
    return false;
  } catch (err) {
    messageError(translate('action_user_update-company-action_error-2'));
    notificationErr(companyError.update_company_002, translate);
    return false;
  } finally {
    if (newSocialMedia) {
      try {
        const socialMediaFetch = await apiCompany.addSocialMediaAPI({
          companyId,
          items: newSocialMedia,
          token,
        });
        if (socialMediaFetch.response.status === 200) {
          for (const item of socialMediaFetch.data) {
            const socialMediaItem: TSocialMedia = {
              id: item.id,
              name: item.name,
              url: item.url,
              client_id: item.client_id,
            };
            dispatch({
              type: CompanyTypes.ADD_SOCIAL_MEDIA,
              payload: socialMediaItem,
            });
          }
        }
      } catch (err) {
        messageError(translate('action_user_update-company-action_error-3'));
        notificationErr(companyError.add_social_media_001, translate);
      }
    }
  }
}

export async function updateCompanyLogoAction({
  dispatch,
  clientId,
  image,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TcompanyDispatchReducer>;
  clientId: string;
  image: File;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    const update = await apiCompany.updateCompanyLogoAPI({ clientId, image, token });
    if (update.response.status === 200) {
      messageSuccess(translate('action_user_update-company-logo-action_success'));
      dispatch({
        type: CompanyTypes.UPDATE_COMPANY_LOGO,
        payload: update.data,
      });
      return true;
    }
    messageError(translate('action_user_update-company-logo-action_error-1'));
    notificationErr(companyError.update_company_logo_002, translate);
    return false;
  } catch (err) {
    messageError(translate('action_user_update-company-logo-action_error-1'));
    notificationErr(companyError.update_company_logo_001, translate);
    return false;
  }
}

export async function updateSocialMediaAction({
  dispatch,
  socialMediaState,
  socialMedia,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TcompanyDispatchReducer>;
  socialMediaState: Array<TSocialMedia>;
  socialMedia: TSocialMedia;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  const { id, url, name } = socialMedia;
  try {
    const updateFetch = await apiCompany.updateSocialMediaAPI({
      socialMedia,
      token,
    });
    if (updateFetch.response.status === 200 && updateFetch.data[0] === 1) {
      messageSuccess(translate('action_user_update-social-media-action_success'));
      const copyOfSocialMediaState: Array<TSocialMedia> = JSON.parse(
        JSON.stringify(socialMediaState)
      );
      const findSocialMedia = copyOfSocialMediaState.findIndex((el) => el.id === id);
      copyOfSocialMediaState[findSocialMedia].url = url;
      copyOfSocialMediaState[findSocialMedia].name = name;
      dispatch({
        type: CompanyTypes.SET_SOCIAL_MEDIA,
        payload: copyOfSocialMediaState,
      });
      return true;
    }
    messageError(translate('action_user_update-social-media-action_error'));
    notificationErr(companyError.updateSocialMedia_002, translate);
    return false;
  } catch (err) {
    messageError(translate('action_user_update-social-media-action_error'));
    notificationErr(companyError.updateSocialMedia_001, translate);
    return false;
  }
}

export async function removeSocialMediaAction({
  dispatch,
  socialMediaState,
  socialMediaId,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TcompanyDispatchReducer>;
  socialMediaState: Array<TSocialMedia>;
  socialMediaId: string;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    const removeFetch = await apiCompany.removeSocialMediaAPI({ socialMediaId, token });
    if (removeFetch.response.status === 200 && removeFetch.data === 1) {
      const filteredCompany = socialMediaState.filter((el) => el.id !== socialMediaId);
      messageSuccess(translate('action_user_remove-social-media-action_success'));
      dispatch({
        type: CompanyTypes.SET_SOCIAL_MEDIA,
        payload: filteredCompany,
      });
      return true;
    }
    messageError(translate('action_user_remove-social-media-action_error'));
    notificationErr(companyError.removeSocialMedia_002, translate);
    return false;
  } catch (err) {
    notificationErr(companyError.removeSocialMedia_001, translate);
    messageError(translate('action_user_remove-social-media-action_error'));
    return false;
  }
}

export async function selectCompanySuperAdminAction({
  dispatch,
  client,
  translate,
  token,
}: {
  dispatch: React.Dispatch<TcompanyDispatchReducer>;
  client: IgetClientsAPIResponseDataItem;
  translate: ITranslate;
  token: string;
}): Promise<boolean> {
  dispatch({ type: CompanyTypes.SET_IS_LOADING, payload: true });
  try {
    const companyFetch = await apiCompany.getCompanyInfoAPI({
      clientId: client.id,
      token,
    });
    if (companyFetch.response.status === 200) {
      dispatch({
        type: CompanyTypes.GET_COMPANY_DATA_SUPER_ADMIN,
        payload: {
          company: client,
          client_social_media: companyFetch.data.client_social_media,
        },
      });
      return true;
    }
    notificationErr(companyError.selectCompanySuperAdmin_002, translate);
    return false;
  } catch (err) {
    notificationErr(companyError.selectCompanySuperAdmin_001, translate);
    return false;
  } finally {
    dispatch({ type: CompanyTypes.SET_IS_LOADING, payload: false });
  }
}
